// ===========================================
// konakla - Tarih Bazlı Fiyatlandırma Yardımcıları
// ===========================================
//
// pricing_rules DB'de `{ rules: [...] }` objesi olarak saklanır (create/PUT bunu
// yazar). Eski/olası düz dizi biçimi de tolere edilir. Tüm OKUMA noktaları bu
// helper'larla düz `DatePricingRule[]` elde eder — böylece "expected array,
// received object" tipi uyumsuzluklar ve eksik tüketim ortadan kalkar.

import type { DatePricingRule } from "@/types";

/** Ham pricing_rules değerini (obje sarmalı, düz dizi veya null) düz diziye normalize eder. */
export function normalizePricingRules(raw: unknown): DatePricingRule[] {
  const arr: unknown[] = Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object" && Array.isArray((raw as { rules?: unknown }).rules)
      ? ((raw as { rules: unknown[] }).rules ?? [])
      : [];

  return arr.filter(
    (r): r is DatePricingRule =>
      !!r &&
      typeof r === "object" &&
      typeof (r as DatePricingRule).startDate === "string" &&
      typeof (r as DatePricingRule).endDate === "string" &&
      typeof (r as DatePricingRule).price === "number"
  );
}

// ===========================================
// Tarih karşılaştırması — SAAT DİLİMİNDEN BAĞIMSIZ
// ===========================================
//
// Y-22 (canlı olarak yeniden üretildi): fiyat hesabı MİSAFİRİN SAAT DİLİMİNE
// göre değişiyordu. Aynı ilan, aynı 7 gece (1–8 Eylül), aynı kural:
//   Europe/Istanbul → 130.000    America/New_York → 120.000
//
// Sebep tip karışımıydı. `new Date("2026-07-13")` — tarih-yalnız ISO dizesi —
// spec gereği **UTC gece yarısı** olarak ayrıştırılır; ardından gelen
// `.setHours(0,0,0,0)` ise **yerel** gece yarısına çeker. Negatif UTC ofsetli
// bir tarayıcıda o UTC anı bir önceki yerel güne düştüğü için kuralın sınırları
// bir gün geriye kayıyordu. Misafirin takvimden seçtiği gece ise yerel bir
// Date olduğu için kaymıyordu → iki taraf farklı takvimlerde karşılaştırılıyordu.
//
// ⚠️ DENETİM RAPORUNUN ÖNERİSİ (`formatDateInIstanbul` ile normalize et) YANLIŞ
// ve BİLEREK UYGULANMADI. Ölçüldü: misafir 18 Ağustos'u seçtiğinde o yaklaşım
// UTC+3'ten DOĞUDAKİ her yerde bir gün geri kayıyor —
//   Asia/Tokyo · Pacific/Auckland · Pacific/Kiritimati → "2026-08-17"
// yani New York'u düzeltirken Tokyo'yu bozardı.
//
// Doğru çözüm: her iki tarafı da **düz takvim günü** (yalın `YYYY-MM-DD`) olarak
// ele almak; hiçbir yerde "an" (instant) aritmetiği yapmamak. Kural tarihleri
// zaten `YYYY-MM-DD` (canlı veride 8 ilanın tamamında, tam 10 karakter), gecenin
// günü de takvimden seçilen yerel gün. Dizeler sözlük sırasına göre
// karşılaştırılabilir olduğu için sınır kontrolü doğrudan çalışır.

/** Bir `Date`'in **yerel** takvim gününü `YYYY-MM-DD` olarak verir. */
function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Kural tarihini karşılaştırılabilir anahtara indirger. Canlı veride değerler
 * tam olarak `YYYY-MM-DD`; olası bir saat bileşenine karşı savunma amaçlı
 * ilk 10 karakter alınır.
 */
function toRuleKey(raw: string): string {
  return raw.slice(0, 10);
}

/** Aşırı uzun aralıklarda sonsuz döngüye karşı tavan (~2 yıl). */
const MAX_NIGHTS = 800;

/** Verilen takvim günü için geçerli fiyat. */
function priceForDateKey(key: string, rules: DatePricingRule[], basePrice: number): number {
  for (const rule of rules) {
    if (key >= toRuleKey(rule.startDate) && key <= toRuleKey(rule.endDate)) return rule.price;
  }
  return basePrice;
}

/**
 * Belirli bir gece için geçerli fiyat. Tarih bir kuralın [startDate, endDate]
 * aralığına (her iki uç dahil) denk geliyorsa kuralın fiyatı, yoksa baz fiyat.
 *
 * `date`'in **yerel takvim günü** okunur — yani misafirin takvimden seçtiği gün.
 */
export function priceForNight(date: Date, rules: DatePricingRule[], basePrice: number): number {
  return priceForDateKey(toDateKey(date), rules, basePrice);
}

/**
 * checkIn (dahil) → checkOut (hariç) arası her gece için fiyatı toplar.
 * Tarih bazlı kurallar gece gece uygulanır (aralık birden fazla dönemi kapsayabilir).
 */
export function accommodationTotal(
  checkIn: Date,
  checkOut: Date,
  rules: DatePricingRule[],
  basePrice: number
): number {
  // Sayaç UTC üzerinden ilerletilir: gün ekleme aritmetiği böylece YAZ SAATİ
  // geçişlerinden etkilenmez (yerel gece yarısı bazı zonlarda hiç var olmayabilir
  // ya da iki kez yaşanabilir). Gün bileşenleri yerelden alındığı için sonuç
  // yine misafirin seçtiği takvim günleridir.
  const cur = new Date(Date.UTC(checkIn.getFullYear(), checkIn.getMonth(), checkIn.getDate()));
  const stop = Date.UTC(checkOut.getFullYear(), checkOut.getMonth(), checkOut.getDate());

  let total = 0;
  let nights = 0;
  while (cur.getTime() < stop && nights < MAX_NIGHTS) {
    total += priceForDateKey(cur.toISOString().slice(0, 10), rules, basePrice);
    cur.setUTCDate(cur.getUTCDate() + 1);
    nights++;
  }
  return total;
}
